/**
 * Single source of truth for how inventory-item statuses map to the categories
 * shown in the item list. Consumed by:
 *  - InventoryList        — grouping + rendering order of the category sections
 *  - useInventoryItem     — whether an item's consolidation link opens History
 *  - searchFormik (items) — which categories an items search asks for
 *  - storageSaga          — the status scope of each category's own request
 *
 * Inventory's auto-select priority repeats these statuses inline instead of
 * importing them; it compares against `.toLowerCase()`, so keep that in mind
 * before syncing it with the casing here.
 *
 * Values are the API's own spelling of `status`: they are sent verbatim as
 * Ransack `q[status_in]` values, and matched case-insensitively against the
 * `status` the API returns.
 */
export type ItemStatusGroup = "In storage" | "Packed" | "Shipped";

export const ITEM_STATUS_GROUPS: Record<ItemStatusGroup, string[]> = {
  "In storage": ["Active", "Problem"],
  Packed: ["Packed", "PreConsolidated", "PrePaid", "Paid"],
  Shipped: ["Shipped"],
};

/** Section labels in display order. */
export const ITEM_STATUS_GROUP_ORDER = Object.keys(
  ITEM_STATUS_GROUPS,
) as ItemStatusGroup[];

/** Categories listed before the user searches or filters. */
export const DEFAULT_ITEM_STATUS_GROUPS: ItemStatusGroup[] = ["In storage"];

const STATUS_TO_GROUP: Record<string, ItemStatusGroup> = Object.entries(
  ITEM_STATUS_GROUPS,
).reduce(
  (lookup, [group, statuses]) => {
    statuses.forEach((status) => {
      lookup[status.toLowerCase()] = group as ItemStatusGroup;
    });
    return lookup;
  },
  {} as Record<string, ItemStatusGroup>,
);

/** Maps an API status value (any case) to its section, or undefined if unlisted. */
export const getItemStatusGroup = (
  status?: string | null,
): ItemStatusGroup | undefined =>
  status ? STATUS_TO_GROUP[status.toLowerCase()] : undefined;
